// ---------------------------------------------------------------------------
// WHICH UNITS THIS DRIVER READS IN.
//
// Pleasant shipped US-only, so `units.ts` hardcoded miles and feet and every
// distance in the app went through it — or, in about eighteen places, around
// it. That was fine while every user was American. It is not fine now: the
// scenery layer covers France, Italy, Belgium, Switzerland, Austria, the
// Netherlands and Britain, and a French driver is being told their drive is
// "3.4 mi".
//
// THREE SYSTEMS, NOT TWO. Morgan, Aug 18, asked about Britain directly:
// "miles and yards, like the road signs". British road signs are in miles and
// yards while everything else in the country is metric, and every UK sat-nav
// follows the signs. So "imperial or metric" is the wrong question and would
// have produced either kilometres on a British motorway or feet on a British
// slip road. Both would read as a bug.
//
//   us      miles + feet     what the app has always done
//   uk      miles + yards    the road signs, and what iOS does for a GB phone
//   metric  kilometres + metres
//
// THE USER IS ASKED, ONCE. Morgan, Aug 18, chose an explicit question during
// onboarding over silently reading the phone's region. A guess that is wrong
// is worse than a question that is short, and someone driving in a country
// they do not live in is exactly the person a guess gets wrong.
//
// SO WHAT RUNS BEFORE THEY ANSWER? Onboarding itself shows example distances,
// and a returning user from before this shipped has never been asked. Those
// screens have to print something. `suggestUnitSystem()` reads the phone's
// region and is used for two things ONLY: to preselect the answer in the
// question, so most people confirm rather than choose, and to format anything
// that renders before the question is answered. It is never written to
// storage — `getUnitSystem()` keeps returning null until a human actually
// picks, so "not asked yet" and "chose miles" stay different facts.
// ---------------------------------------------------------------------------

export type UnitSystem = "us" | "uk" | "metric";

export const UNIT_SYSTEMS: UnitSystem[] = ["us", "uk", "metric"];

/** What to show on the picker. Concrete examples beat the words "imperial"
 *  and "metric", which people read past. */
export const UNIT_LABELS: Record<UnitSystem, { label: string; example: string }> = {
  us:     { label: "Miles and feet",   example: "3.4 mi · 500 ft" },
  uk:     { label: "Miles and yards",  example: "3.4 mi · 500 yd" },
  metric: { label: "Kilometres and metres", example: "5.5 km · 150 m" },
};

const KEY = "pleasant.units";

/**
 * Regions that sign their roads in miles. The UK is the only one of these that
 * is otherwise metric, which is why it gets its own system rather than being
 * folded into `us`.
 *
 * Liberia and Myanmar are the other two non-metric countries and are here for
 * completeness, not because anyone is driving Pleasant there — if that ever
 * changes, the honest thing is a real look at their signage rather than an
 * assumption inherited from a trivia list.
 */
const MILES_REGIONS = new Set(["US", "LR", "MM", "PR", "GU", "VI", "AS", "MP"]);

/** Reads the phone's region. Never stored — see the header. */
export function suggestUnitSystem(): UnitSystem {
  if (typeof navigator === "undefined") return "us";
  try {
    const tag =
      navigator.language ||
      (Array.isArray(navigator.languages) ? navigator.languages[0] : "") ||
      "en-US";
    // "en-GB" → GB, "fr" → undefined, "en-US-u-ca-gregory" → US.
    const region = new Intl.Locale(tag).region ?? tag.split("-")[1]?.toUpperCase();
    if (region === "GB") return "uk";
    if (region && MILES_REGIONS.has(region)) return "us";
    // No region in the tag (a bare "fr", "de") is metric: every language
    // without a region attached belongs to a metric country except English,
    // and a bare "en" is ambiguous enough that the US default is the safer
    // read for a US-launched app.
    if (!region) return tag.toLowerCase().startsWith("en") ? "us" : "metric";
    return "metric";
  } catch {
    return "us";
  }
}

/** The explicit choice, or null when the driver has never been asked. The
 *  null is load-bearing: it is how onboarding knows to ask. */
export function getUnitSystem(): UnitSystem | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(KEY);
    return v === "us" || v === "uk" || v === "metric" ? v : null;
  } catch {
    return null;
  }
}

/** What to FORMAT with right now — the choice if there is one, otherwise the
 *  phone's region. Every formatter calls this; only onboarding calls
 *  `getUnitSystem` directly. */
export function resolveUnitSystem(): UnitSystem {
  return getUnitSystem() ?? suggestUnitSystem();
}

export function setUnitSystem(system: UnitSystem): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, system);
    // Distances are rendered all over the tree by components that have no
    // reason to share state. A plain event is cheaper than threading a context
    // through forty files, and the setting changes at most once or twice in a
    // lifetime.
    window.dispatchEvent(new CustomEvent("pleasant:unitschange", { detail: system }));
  } catch {
    /* private mode — the app still works, the choice just will not survive */
  }
}

/** Has this driver been asked yet? */
export function hasChosenUnits(): boolean {
  return getUnitSystem() !== null;
}
